import { useState, useRef, useEffect } from 'react'
import { Filter, Eye, EyeOff } from 'lucide-react'
import BugStatusFilter from './BugStatusFilter'
import BugKindFilter from './BugKindFilter'
import BugSeverityFilter from './BugSeverityFilter'
import BugEnvironmentFilter from './BugEnvironmentFilter'
import type { ProjectEnvironment } from '../services/projectDataApi'
import './BugFilterMenu.css'

interface BugFilterMenuProps {
  statusFilter: string
  onStatusChange: (value: string) => void
  kindFilter: string[]
  onKindChange: (value: string[]) => void
  severityFilter: string
  onSeverityChange: (value: string) => void
  hideResolved: boolean
  onHideResolvedToggle: () => void
  resolvedCount: number
  // When true (mobile) the menu also holds the primary Status + Severity controls, which otherwise
  // sit inline in the toolbar. On desktop the menu only carries the secondary filters.
  includePrimary: boolean
  environmentFilter: string
  onEnvironmentChange: (value: string) => void
  environments: ProjectEnvironment[]
}

// The toolbar's funnel/"filter" popover. On desktop it only carries Show Resolved — Status, Severity
// and Kind all sit inline in the toolbar. On mobile, where those inline controls are hidden, it
// carries every filter.
export default function BugFilterMenu({
  statusFilter,
  onStatusChange,
  kindFilter,
  onKindChange,
  severityFilter,
  onSeverityChange,
  hideResolved,
  onHideResolvedToggle,
  resolvedCount,
  includePrimary,
  environmentFilter,
  onEnvironmentChange,
  environments,
}: BugFilterMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const activeFlags = includePrimary
    ? [statusFilter !== 'all', kindFilter.length > 0, severityFilter !== 'all', environmentFilter !== 'all', !hideResolved]
    : [!hideResolved]
  const activeCount = activeFlags.filter(Boolean).length

  return (
    <div className="bug-filter-menu" ref={ref} data-testid="bug-filter-menu">
      <button
        type="button"
        className={`bug-filter-menu__trigger ${activeCount > 0 ? 'bug-filter-menu__trigger--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Filters"
        title="Filters"
        data-testid="bug-filter-menu-trigger"
        data-analytics-event="bug-filter-menu-toggle"
      >
        <Filter size={15} aria-hidden="true" />
        {activeCount > 0 && <span className="bug-filter-menu__badge">{activeCount}</span>}
      </button>
      {open && (
        <div
          className="bug-filter-menu__panel"
          role="menu"
          aria-label="Bug filters"
          data-testid="bug-filter-menu-panel"
        >
          {includePrimary && (
            <>
              <div className="bug-filter-menu__row">
                <span className="bug-filter-menu__label">Status</span>
                <BugStatusFilter value={statusFilter} onChange={onStatusChange} />
              </div>
              <div className="bug-filter-menu__row">
                <span className="bug-filter-menu__label">Severity</span>
                <BugSeverityFilter value={severityFilter} onChange={onSeverityChange} />
              </div>
              <div className="bug-filter-menu__row">
                <span className="bug-filter-menu__label">Kind</span>
                <BugKindFilter value={kindFilter} onChange={onKindChange} />
              </div>
              <div className="bug-filter-menu__row">
                <span className="bug-filter-menu__label">Environment</span>
                <BugEnvironmentFilter value={environmentFilter} onChange={onEnvironmentChange} environments={environments} />
              </div>
            </>
          )}
          <div className="bug-filter-menu__row">
            <span className="bug-filter-menu__label">Resolved</span>
            <button
              type="button"
              className={`bug-list-tab__resolved-toggle ${hideResolved ? '' : 'bug-list-tab__resolved-toggle--active'}`}
              onClick={onHideResolvedToggle}
              data-testid="hide-resolved-toggle"
              data-analytics-event="bug-hide-resolved-toggle"
              aria-pressed={!hideResolved}
            >
              {hideResolved ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
              {hideResolved ? `Resolved${resolvedCount > 0 ? ` (${resolvedCount})` : ''}` : 'Resolved'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
